import _ from "lodash";
import React, {
  ButtonHTMLAttributes,
  CSSProperties,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ArcherContainer,
  ArcherContainerRef,
  ArcherElement,
} from "react-archer";
import {
  HiCheck,
  HiMiniArrowLeft,
  HiMiniArrowRight,
  HiMiniPlus,
  HiMiniXMark,
} from "react-icons/hi2";

import {
  Resource,
  Sharing,
  State,
  defaultRoleInheritanceConfiguration,
} from "@/src/modeler/state";
import AnimatedPanels from "./AnimatedPanels";
import { capitalize, deleteIdx, updateIdx } from "@/src/workbenchLogic";
import ResourceHierarchy, { REFRESH_PERIOD_MS } from "./ResourceHierarchy";
import {
  SharedWithParent,
  SharedWithPublic,
  SharedWithUser,
} from "./AuthorizationFlowDiagram";
import Button, { ButtonTheme } from "@/components/Button";
import { ResourcePermissionsEditor, ResourceRolesEditor } from "pages/modeler";

export function personalized(
  strings: TemplateStringsArray,
  ...personalizations: (string | undefined)[]
): React.JSX.Element {
  return (
    <>
      {strings.map((s, i) => (
        <span key={i}>
          {s}
          <span className="font-semibold">{personalizations[i]}</span>
        </span>
      ))}
    </>
  );
}

enum Chapter {
  ResourceHierarchy = "Resources",
  Permissions = "Permissions",
  Sharing = "Sharing",
  CustomizeModel = "Customize",
}

type QuestionProps = {
  goBack: () => void;
  setChapter: Dispatch<SetStateAction<Chapter>>;
  setHighlightedResource: Dispatch<SetStateAction<string | undefined>>;
  state: State;
  pushQuestionAndAnswer: (
    question: React.FC<QuestionProps> | null,
    maybeAnswer?: State
  ) => void;
};

const Question: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="flex flex-col space-y-4 lg:space-y-6">{children}</div>
);
const QuestionHeading: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="text-xl">{children}</div>
);
const QuestionSubHeading: React.FC<React.PropsWithChildren> = ({
  children,
}) => <div className="text-l text-muted-foreground">{children}</div>;
const QuestionOption: React.FC<
  React.PropsWithChildren<{
    arrow?: "left" | "right";
    disabled?: boolean;
    onClick: () => void;
    theme: ButtonTheme;
    type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  }>
> = ({ arrow, children, disabled, onClick, theme, type }) => (
  <Button
    disabled={disabled}
    medium
    onClick={onClick}
    theme={theme}
    type={type ?? "button"}
  >
    {arrow === "left" ? <HiMiniArrowLeft className="mr-1" /> : null}
    {children}
    {arrow === "right" ? <HiMiniArrowRight className="ml-1" /> : null}
  </Button>
);
const QuestionOptions: React.FC<
  React.PropsWithChildren<{ goBack: () => void }>
> = ({ children, goBack }) => (
  <div className="flex justify-center space-x-4">
    <QuestionOption arrow="left" onClick={goBack} theme="tertiary">
      Back
    </QuestionOption>
    {children}
  </div>
);
const QuestionResourceInput: React.FC<{
  defaultValue: string;
  goBack: () => void;
  onSubmit: (value: string) => void;
}> = ({ defaultValue, goBack, onSubmit }) => {
  const [value, setValue] = useState(defaultValue);
  const invalid = value.trim() === "";
  return (
    <form
      className="flex flex-col space-y-4 items-center"
      onSubmit={(e) => {
        e.preventDefault();
        if (invalid) return;
        onSubmit(value);
      }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(capitalize(e.target.value.trim()))}
        className="border rounded px-4 py-2"
        autoFocus
      />
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          arrow="right"
          type="submit"
          disabled={invalid}
          onClick={() => {}}
          theme="primary"
        >
          Next
        </QuestionOption>
      </QuestionOptions>
    </form>
  );
};
const QuestionResourceListInput: React.FC<
  React.PropsWithChildren<{
    goBack: () => void;
    onClick: (resources: string[]) => void;
  }>
> = ({ children, goBack, onClick }) => {
  const [resources, setResources] = useState<string[]>([]);
  const [value, setValue] = useState<string>("");

  const invalid = value === "" || resources.includes(value);

  return (
    <div className="flex flex-col space-y-4 items-center">
      <ul className="list-disc text-left">
        {resources.map((resource, i) => (
          <li key={resource} className="font-semibold">
            <span className="font-semibold mr-4">{resource}</span>
            <Button
              xsmall
              theme="delete"
              onClick={() => setResources(deleteIdx(resources, i))}
            >
              <HiMiniXMark />
            </Button>
          </li>
        ))}
        <li>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (invalid) return;
              setResources([...resources, value]);
              setValue("");
            }}
          >
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(capitalize(e.target.value.trim()))}
              className="rounded mr-4 py-1 px-2"
              placeholder="Add a resource"
              autoFocus
            />
            <Button
              medium
              disabled={invalid}
              theme={resources.length ? "secondary" : "primary"}
            >
              <HiMiniPlus />
            </Button>
          </form>
        </li>
      </ul>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          arrow="right"
          disabled={resources.length === 0}
          onClick={() =>
            value === ""
              ? onClick(resources)
              : onClick(_.uniq([...resources, value]))
          }
          theme={resources.length ? "primary" : "secondary"}
        >
          {children}
        </QuestionOption>
      </QuestionOptions>
    </div>
  );
};

// QUESTIONS

const DoesThisCorrectlyDescribeSharing: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
  state,
}) => {
  setChapter(Chapter.Sharing);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>Does this look right?</QuestionHeading>
      <div className="flex justify-center text-start">
        <ul className="list-disc pl-4">
          {state.resources.flatMap((resource) => {
            if (resource.sharing === null) return [];
            const items = [];
            if (resource.sharing.canShareWithOtherUsers) {
              items.push(
                <li key={`${resource.name}-other-users`}>
                  {personalized`A user can access a particular ${resource.name} if it is shared
              with them`}
                </li>
              );
            }
            if (resource.sharing.canShareWithParent) {
              items.push(
                ...resource.relations.map((relation) => (
                  <li key={`${resource.name}-${relation.resource}-parent`}>
                    {personalized`A user can access a particular ${resource.name} if it is shared with
                an associated ${relation.resource} they have access to`}
                  </li>
                ))
              );
            }
            if (resource.sharing.canSharePublic) {
              items.push(
                <li key={`${resource.name}-public`}>
                  {personalized`Everyone can access a publicly-shared ${resource.name}`}
                </li>
              );
            }
            return items;
          })}
        </ul>
      </div>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          arrow="right"
          onClick={() => {
            pushQuestionAndAnswer(null);
          }}
          theme="primary"
        >
          Yes
        </QuestionOption>
      </QuestionOptions>
    </Question>
  );
};

const INSET_SHADOW_STYLE: CSSProperties = {
  boxShadow: "inset 0 20px 25px -5px rgb(0 0 0 / 0.1)",
};

const HowCanThisResourceBeShared: (
  resource: string,
  k: React.FC<QuestionProps>
) => React.FC<QuestionProps> = (resource, k) =>
  function HCTRB({
    goBack,
    pushQuestionAndAnswer,
    setChapter,
    setHighlightedResource,
    state,
  }) {
    const [sharing, setSharing] = useState<Sharing>({
      canShareWithOtherUsers: null,
      canShareWithParent: [],
      canSharePublic: null,
    });
    const resourceBlock = state.resources.find((r) => r.name === resource);
    if (!resourceBlock) throw new Error("Oh ye gads my roast is ruined");
    setChapter(Chapter.Sharing);
    setHighlightedResource(resource);
    return (
      <Question>
        <QuestionHeading>{personalized`How may a user share a particular ${resource} to other users? Select all that apply.`}</QuestionHeading>
        <div className="items-center space-x-4 overflow-x-auto flex pb-8">
          <div
            className={`shrink-0 w-96 rounded-xl transition-colors hover:bg-muted cursor-pointer ${
              sharing.canShareWithOtherUsers ? "bg-muted" : "shadow-xl"
            }`}
            style={
              sharing.canShareWithOtherUsers ? INSET_SHADOW_STYLE : undefined
            }
            onClick={() => {
              setSharing({
                ...sharing,
                canShareWithOtherUsers: sharing.canShareWithOtherUsers
                  ? null
                  : [resourceBlock.roles[0]],
              });
            }}
          >
            <div className="px-10 pt-10 text-center">
              Directly to another user.
            </div>
            <SharedWithUser resource={resource} />
          </div>
          {resourceBlock.relations.map(({ resource: parent }) => {
            const canShareWithThisParent = sharing.canShareWithParent.find(
              (sharingConfig) => sharingConfig.parent === parent
            );
            return (
              <div
                className={`shrink-0 w-96 rounded-xl transition-colors hover:bg-muted cursor-pointer ${
                  canShareWithThisParent ? "bg-muted" : "shadow-xl"
                }`}
                style={canShareWithThisParent ? INSET_SHADOW_STYLE : undefined}
                onClick={() => {
                  const canShareWithParent = canShareWithThisParent
                    ? sharing.canShareWithParent.filter(
                        (sharingConfig) => sharingConfig.parent != parent
                      )
                    : [
                        ...sharing.canShareWithParent,
                        {
                          parent,
                          configuration: defaultRoleInheritanceConfiguration(
                            state,
                            resource,
                            parent
                          ),
                        },
                      ];
                  setSharing({
                    ...sharing,
                    canShareWithParent,
                  });
                }}
                key={parent}
              >
                <div className="px-10 pt-10 text-center">
                  {personalized`To its parent ${parent}.`}
                </div>
                <SharedWithParent child={resource} parent={parent} />
              </div>
            );
          })}
          <div
            className={`shrink-0 w-96 rounded-xl transition-colors hover:bg-muted cursor-pointer ${
              sharing.canSharePublic ? "bg-muted" : "shadow-xl"
            }`}
            style={sharing.canSharePublic ? INSET_SHADOW_STYLE : undefined}
            onClick={() => {
              setSharing({
                ...sharing,
                canSharePublic: sharing.canSharePublic
                  ? null
                  : [resourceBlock.permissions[0]],
              });
            }}
          >
            <div className="px-10 pt-10 text-center">Publicly.</div>
            <SharedWithPublic resource={resource} />
          </div>
        </div>
        <QuestionOptions goBack={goBack}>
          <QuestionOption
            arrow="right"
            disabled={
              !sharing.canShareWithOtherUsers &&
              !sharing.canShareWithParent &&
              !sharing.canSharePublic
            }
            onClick={() => {
              const existingResourceIdx = state.resources.findIndex(
                (r) => r.name === resource
              );
              const resources = updateIdx(
                state.resources,
                existingResourceIdx,
                {
                  ...state.resources[existingResourceIdx],
                  sharing,
                }
              );
              pushQuestionAndAnswer(k, { ...state, resources });
            }}
            theme="primary"
          >
            Next
          </QuestionOption>
        </QuestionOptions>
      </Question>
    );
  };

const WhichResourcesCanBeShared: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
  state,
}) => {
  const [shareableResources, setShareableResources] = useState<string[]>([]);
  setChapter(Chapter.Sharing);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>Which resources can users share?</QuestionHeading>
      <QuestionSubHeading>
        For example, shared publicly or with particular users.
      </QuestionSubHeading>
      <div className="flex justify-center">
        <div>
          {state.resources.map((resource) => (
            <label key={resource.name} className="block text-start">
              <input
                type="checkbox"
                onChange={() => {
                  if (shareableResources.includes(resource.name)) {
                    setShareableResources(
                      shareableResources.filter((r) => r !== resource.name)
                    );
                  } else {
                    setShareableResources([
                      ...shareableResources,
                      resource.name,
                    ]);
                  }
                }}
                checked={shareableResources.includes(resource.name)}
              />
              <span className="ml-2">{resource.name}</span>
            </label>
          ))}
        </div>
      </div>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          arrow="right"
          onClick={() => {
            const questions = shareableResources.reduceRight(
              (acc, r) => HowCanThisResourceBeShared(r, acc),
              DoesThisCorrectlyDescribeSharing
            );
            pushQuestionAndAnswer(questions);
          }}
          theme="primary"
        >
          Next
        </QuestionOption>
      </QuestionOptions>
    </Question>
  );
};

const DoesThisHierarchyLookCorrect: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
}) => {
  setChapter(Chapter.ResourceHierarchy);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>
        Does the model on the right look correct?
      </QuestionHeading>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          arrow="right"
          onClick={() => pushQuestionAndAnswer(DefaultPermissions)}
          theme="primary"
        >
          Yes
        </QuestionOption>
      </QuestionOptions>
    </Question>
  );
};

const DefaultPermissions: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
  state,
}) => {
  setChapter(Chapter.Permissions);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>
        We configured your resources with some default roles and permissions.
      </QuestionHeading>
      <QuestionHeading>You can customize these now or later.</QuestionHeading>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          onClick={() => pushQuestionAndAnswer(ThinkAboutHowUsersGainAccess)}
          theme="secondary"
        >
          Later
        </QuestionOption>
        <QuestionOption
          onClick={() => {
            const questions = state.resources.reduceRight(
              (acc, r) => CustomizePermissionsForResource(r.name, acc),
              ThinkAboutHowUsersGainAccess
            );
            pushQuestionAndAnswer(questions);
          }}
          theme="primary"
        >
          Now
        </QuestionOption>
      </QuestionOptions>
    </Question>
  );
};

const CustomizePermissionsForResource: (
  resource: string,
  k: React.FC<QuestionProps>
) => React.FC<QuestionProps> = (resource, k) =>
  function CPFR({
    goBack,
    pushQuestionAndAnswer,
    setChapter,
    setHighlightedResource,
    state,
  }) {
    const [localState, setLocalState] = useState(state);
    setChapter(Chapter.Permissions);
    setHighlightedResource(resource);
    return (
      <Question>
        <QuestionHeading>{personalized`Customize roles and permissions for ${resource}`}</QuestionHeading>
        <ResourceRolesEditor
          state={localState}
          setState={setLocalState}
          resourceName={resource}
          track={() => {}}
        />
        <ResourcePermissionsEditor
          state={localState}
          setState={setLocalState}
          resourceName={resource}
          track={() => {}}
        />
        <QuestionOptions goBack={goBack}>
          <QuestionOption
            arrow="right"
            disabled={false}
            onClick={() => pushQuestionAndAnswer(k, localState)}
            theme="primary"
          >
            Next
          </QuestionOption>
        </QuestionOptions>
      </Question>
    );
  };

const ThinkAboutHowUsersGainAccess: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
}) => {
  setChapter(Chapter.Sharing);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>
        Now let{"'"}s model sharing & collaboration in your app.
      </QuestionHeading>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          arrow="right"
          onClick={() => pushQuestionAndAnswer(WhichResourcesCanBeShared)}
          theme="primary"
        >
          Next
        </QuestionOption>
      </QuestionOptions>
    </Question>
  );
};

const WhatIsAnotherResourceYouGrantAccessTo: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
  state,
}) => {
  setChapter(Chapter.ResourceHierarchy);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>
        What{"'"}s another resource you can grant users access to?
      </QuestionHeading>
      <QuestionResourceInput
        defaultValue=""
        goBack={goBack}
        onSubmit={(resource) => {
          const existingIdx = state.resources.findIndex(
            ({ name }) => name === resource
          );
          if (existingIdx >= 0) {
            const resources = updateIdx(state.resources, existingIdx, {
              ...state.resources[existingIdx],
              isGrantedAccessTo: true,
            });
            pushQuestionAndAnswer(AreThereOtherResourcesYouGrantAccessTo, {
              resources,
            });
          } else {
            const newResource: Resource = {
              name: resource,
              isGrantedAccessTo: true,
              relations: [],
              roles: ["member", "admin"],
              permissions: ["view", "edit"],
              rolePermissionImplications: [
                {
                  role: "member",
                  permission: "view",
                },
                {
                  role: "admin",
                  permission: "view",
                },
                {
                  role: "admin",
                  permission: "edit",
                },
              ],
              sharing: {
                canShareWithOtherUsers: null,
                canShareWithParent: [],
                canSharePublic: null,
              },
            };
            const resources = [...state.resources, newResource];
            pushQuestionAndAnswer(
              DoesAccessToThisImplyAccessToOtherResources(
                resource,
                AreThereOtherResourcesYouGrantAccessTo
              ),
              { resources }
            );
          }
        }}
      />
    </Question>
  );
};

const AreThereOtherResourcesYouGrantAccessTo: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
}) => {
  setChapter(Chapter.ResourceHierarchy);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>
        Are there other resources you can grant users access to?
      </QuestionHeading>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          onClick={() => pushQuestionAndAnswer(DoesThisHierarchyLookCorrect)}
          theme="secondary"
        >
          No
        </QuestionOption>
        <QuestionOption
          onClick={() =>
            pushQuestionAndAnswer(WhatIsAnotherResourceYouGrantAccessTo)
          }
          theme="secondary"
        >
          Yes
        </QuestionOption>
      </QuestionOptions>
    </Question>
  );
};

const WhatResourcesDoesThisImplyAccessTo: (
  resource: string,
  k: React.FC<QuestionProps>
) => React.FC<QuestionProps> = (resource, k) =>
  function WRDTIAT({
    goBack,
    pushQuestionAndAnswer,
    setChapter,
    setHighlightedResource,
    state,
  }) {
    setChapter(Chapter.ResourceHierarchy);
    setHighlightedResource(resource);
    return (
      <Question>
        <QuestionHeading>
          Which other resources do they get access to?
        </QuestionHeading>
        <QuestionResourceListInput
          goBack={goBack}
          onClick={(resourceNames) => {
            const question = resourceNames
              // Filter out resources we've already asked about.
              .filter(
                (r) => state.resources.findIndex(({ name }) => name === r) < 0
              )
              .reduceRight(
                (acc, resource) =>
                  DoesAccessToThisImplyAccessToOtherResources(resource, acc),
                k
              );
            const resources = resourceNames.reduce(
              (resources: Resource[], resourceName: string): Resource[] => {
                const existingResourceIdx = resources.findIndex(
                  ({ name }) => name === resourceName
                );
                if (existingResourceIdx < 0) {
                  return [
                    ...resources,
                    {
                      name: resourceName,
                      isGrantedAccessTo: false,
                      relations: [
                        { resource, roleInheritance: { kind: "simple" } },
                      ],
                      roles: ["member", "admin"],
                      permissions: ["view", "edit"],
                      rolePermissionImplications: [
                        {
                          role: "member",
                          permission: "view",
                        },
                        {
                          role: "admin",
                          permission: "view",
                        },
                        {
                          role: "admin",
                          permission: "edit",
                        },
                      ],
                      sharing: {
                        canShareWithOtherUsers: null,
                        canShareWithParent: [],
                        canSharePublic: null,
                      },
                    },
                  ];
                } else {
                  return updateIdx(resources, existingResourceIdx, {
                    ...resources[existingResourceIdx],
                    relations: [
                      ...resources[existingResourceIdx].relations,
                      {
                        resource,
                        roleInheritance: resources[existingResourceIdx]
                          .isGrantedAccessTo
                          ? {
                              kind: "configured",
                              configuration:
                                defaultRoleInheritanceConfiguration(
                                  state,
                                  resourceName,
                                  resource
                                ),
                            }
                          : { kind: "simple" },
                      },
                    ],
                  });
                }
              },
              state.resources
            );
            pushQuestionAndAnswer(question, { resources });
          }}
        >
          Next
        </QuestionResourceListInput>
      </Question>
    );
  };

const DoesAccessToThisImplyAccessToOtherResources: (
  resource: string,
  k: React.FC<QuestionProps>
) => React.FC<QuestionProps> = (resource, k) =>
  function DATTIATOR({
    goBack,
    pushQuestionAndAnswer,
    setChapter,
    setHighlightedResource,
  }) {
    setChapter(Chapter.ResourceHierarchy);
    setHighlightedResource(resource);
    return (
      <Question>
        <QuestionHeading>{personalized`When users have access to a particular ${resource}, do they automatically get access to other child resources?`}</QuestionHeading>
        <QuestionOptions goBack={goBack}>
          <QuestionOption
            onClick={() => pushQuestionAndAnswer(k)}
            theme="secondary"
          >
            No
          </QuestionOption>
          <QuestionOption
            onClick={() =>
              pushQuestionAndAnswer(
                WhatResourcesDoesThisImplyAccessTo(resource, k)
              )
            }
            theme="secondary"
          >
            Yes
          </QuestionOption>
        </QuestionOptions>
      </Question>
    );
  };

const ToStartOff: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
}) => {
  setChapter(Chapter.ResourceHierarchy);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>
        We{"'"}ll start by building a diagram of the entities in your
        authorization model.
      </QuestionHeading>
      <QuestionOptions goBack={goBack}>
        <QuestionOption
          arrow="right"
          onClick={() => pushQuestionAndAnswer(WhatIsTheTopLevelResource)}
          theme="primary"
        >
          Next
        </QuestionOption>
      </QuestionOptions>
    </Question>
  );
};

const WhatIsTheTopLevelResource: React.FC<QuestionProps> = ({
  goBack,
  pushQuestionAndAnswer,
  setChapter,
  setHighlightedResource,
}) => {
  setChapter(Chapter.ResourceHierarchy);
  setHighlightedResource(undefined);
  return (
    <Question>
      <QuestionHeading>
        What{"'"}s a resource you grant users access to?
      </QuestionHeading>
      <QuestionResourceInput
        defaultValue="Organization"
        goBack={goBack}
        onSubmit={(resource) =>
          pushQuestionAndAnswer(
            DoesAccessToThisImplyAccessToOtherResources(
              resource,
              AreThereOtherResourcesYouGrantAccessTo
            ),
            {
              resources: [
                {
                  name: resource,
                  isGrantedAccessTo: true,
                  relations: [],
                  roles: ["member", "admin"],
                  permissions: ["view", "edit"],
                  rolePermissionImplications: [
                    {
                      role: "member",
                      permission: "view",
                    },
                    {
                      role: "admin",
                      permission: "view",
                    },
                    {
                      role: "admin",
                      permission: "edit",
                    },
                  ],
                  sharing: {
                    canShareWithOtherUsers: null,
                    canShareWithParent: [],
                    canSharePublic: null,
                  },
                },
              ],
            }
          )
        }
      />
    </Question>
  );
};

const ChapterProgress: React.FC<{ current: Chapter }> = ({ current }) => {
  const ref = useRef<ArcherContainerRef>(null);

  // Hack to recompute React Archer arrow positions after animations complete.
  useEffect(() => {
    const interval = setInterval(() => {
      ref.current?.refreshScreen();
    }, REFRESH_PERIOD_MS);
    setTimeout(() => clearInterval(interval), 300);
    return () => clearInterval(interval);
  }, [ref.current]);

  return (
    <ArcherContainer
      strokeColor="#352990"
      strokeDasharray="2,4"
      endMarker={false}
      ref={ref}
    >
      <div className="flex flex-row justify-between font-light">
        {Object.values(Chapter).map((c, i, cs) => {
          const isActive = c === current;
          const currentIdx = cs.findIndex((x) => x === current);
          const isComplete = i < currentIdx;
          return (
            <div
              key={c}
              className="flex flex-col items-center text-center max-w-min space-y-2"
            >
              <ArcherElement
                id={c}
                // Draw lines from chapterN to chapterN+1
                relations={
                  cs[i + 1]
                    ? [
                        {
                          targetId: cs[i + 1],
                          targetAnchor: "left",
                          sourceAnchor: "right",
                        },
                      ]
                    : []
                }
              >
                <div
                  className={`border rounded-full border-[#352990] w-10 h-10 flex items-center justify-center transition-all ${
                    isActive ? "bg-[#352990]" : "bg-[#352990]/10"
                  }`}
                >
                  <span
                    className={`transition-all text-sm ${
                      isActive ? "invert" : "text-brand"
                    }`}
                  >
                    {isComplete ? <HiCheck /> : i + 1}
                  </span>
                </div>
              </ArcherElement>
              <div>{c}</div>
            </div>
          );
        })}
      </div>
    </ArcherContainer>
  );
};

const ModelerQuiz: React.FC<{
  track: (event: string, properties: { [key: string]: unknown }) => void;
  goBackToExamples: () => void;
  setState: (state: State) => void;
}> = ({ track, goBackToExamples, setState }) => {
  // question history stored in chronological order
  const [questionAndAnswers, setQuestionAndAnswers] = useState<
    { Question: React.FC<QuestionProps>; state: State }[]
  >([{ Question: ToStartOff, state: { resources: [] } }]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [chapter, setChapter] = useState(Chapter.ResourceHierarchy);
  const [highlightedResource, setHighlightedResource] = useState<string>();

  const pushQuestionAndAnswer = (
    Question: React.FC<QuestionProps> | null,
    maybeAnswer?: State
  ) => {
    const { Question: lastQuestion, state: lastState } =
      questionAndAnswers[questionIndex];
    track("Modeler - Quiz - Answered Question", {
      from: lastQuestion?.name,
      to: Question?.name,
      answer: maybeAnswer,
    });
    const state = maybeAnswer ?? lastState;
    if (Question) {
      setQuestionAndAnswers([
        ...questionAndAnswers.slice(0, questionIndex + 1),
        { Question, state },
      ]);
      setQuestionIndex(questionIndex + 1);
    } else {
      track("Modeler - Quiz - Complete", {
        state,
      });
      setState(state);
    }
  };
  const goBack = () => {
    track("Modeler - Quiz - Go Back", {
      from: questionAndAnswers[questionIndex]?.Question?.name,
      to: questionAndAnswers[questionIndex - 1]?.Question?.name,
    });
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    } else {
      goBackToExamples();
    }
  };

  const { state } = questionAndAnswers[questionIndex];
  return (
    <div className="grid grid-cols-3 h-full font-light">
      <div className="p-4 lg:p-6 space-y-4 lg:space-y-6 col-span-2 flex flex-col items-center">
        <AnimatedPanels index={questionIndex}>
          {questionAndAnswers.map(({ Question, state }, i) => (
            <Question
              goBack={goBack}
              key={i}
              pushQuestionAndAnswer={pushQuestionAndAnswer}
              setChapter={setChapter}
              setHighlightedResource={setHighlightedResource}
              state={state}
            />
          ))}
        </AnimatedPanels>
        <div className="grow" />
        <div className="w-full cursor-default">
          <ChapterProgress current={chapter} />
        </div>
      </div>
      <div className="col-span-1 border-l p-4 lg:p-6">
        {/* If there are no resources, don't render the ERD. */}
        {state.resources.length === 0 ? null : (
          <ResourceHierarchy
            state={state}
            highlightedResource={highlightedResource}
          />
        )}
      </div>
    </div>
  );
};

export default ModelerQuiz;
